// src/hooks/useFetchRest.js
import { sentryCapture } from '../errorHandlers';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import isEmpty from 'lodash.isempty';
import { isSweedTestMode } from '../sweedConfig';

export default function useFetchRest(endpoint, options = {}) {
    // TEST HARNESS: the `data/menu` endpoint resolves Dutchie special-product IDs
    // server-side. Sweed has no equivalent, so short-circuit it to an empty result
    // (instead of stalling on a Dutchie call) and let products load.
    const sweedShortCircuit = isSweedTestMode() && typeof endpoint === 'string' && endpoint.includes('data/menu');

    let query = {
        queryKey: [endpoint],
        queryFn: async () => {
            if (sweedShortCircuit) {
                return { specialProductsIds: {}, specialBrands: false };
            }
            try {
                const response = await axios.get(`/wp-json/range_headless/v1/${endpoint}`);
                return response.data;
            } catch (error) {
                // Log the error for debugging
                console.error('API request failed:', error);

                // If it's a network error, we might want to handle it differently
                if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
                    // Return a default value instead of throwing
                    console.warn('Network error - returning default data');

                    // Return appropriate default based on endpoint
                    if (endpoint.includes('menu')) {
                        // Match the structure of your empty response
                        return { specialProductsIds: {}, specialBrands: false };
                    }

                    return {};
                }

                throw error;
            }
        },
        throwOnError: (err) => {
            // Don't mark network errors as fatal to prevent infinite reloads
            if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
                console.error('Network error in query:', err);
                return false; // Don't throw, let React Query handle it
            }

            err.level = 'error'; // Changed from 'fatal' to prevent app crashes
            sentryCapture(err);
            return false; // Let React Query handle the error state
        },
        retry: (failureCount, error) => {
            // Don't retry network errors indefinitely
            if (error.code === 'ERR_NETWORK') {
                return failureCount < 2; // Only retry twice for network errors
            }
            return failureCount < 3; // Default retry behavior
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

        // Cache configuration optimized for performance
        staleTime: 30 * 60 * 1000,         // 30 minutes (matches CacheScheduler backend refresh)
        cacheTime: 60 * 60 * 1000,         // 1 hour (keep data in memory even after component unmount)
        refetchOnWindowFocus: false,       // Don't refetch when user switches tabs
        refetchOnMount: false,             // Don't refetch if data is still fresh
        keepPreviousData: true,            // Show stale data while fetching new data (no loading flash)
    };

    if (!isEmpty(options)) {
        query = { ...query, ...options };
    }

    return useQuery(query);
}
